
import { useOnSaleProducts } from '@/hooks/useOnSaleProducts';
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import SaleProductCard from './SaleProductCard';
import { Skeleton } from '@/components/ui/skeleton';
import SectionHeader from './SectionHeader';
import * as React from 'react';

const OnSaleCarousel = () => {
  const { data: products, isLoading, isError } = useOnSaleProducts();
  const [api, setApi] = React.useState<CarouselApi>();
  const [autoplayPlugin, setAutoplayPlugin] = React.useState<any>(null);

  // Dynamically import and initialize Autoplay plugin safely
  React.useEffect(() => {
    const initializeAutoplay = async () => {
      try {
        const { default: Autoplay } = await import("embla-carousel-autoplay");
        if (typeof Autoplay === 'function') {
          setAutoplayPlugin(Autoplay({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: true }));
        }
      } catch (error) {
        console.warn('Failed to load Autoplay plugin:', error);
        // Carousel will work without autoplay
      }
    };

    initializeAutoplay();
  }, []);

  if (isLoading) {
    return (
      <div className="py-12 md:py-20 bg-white/5 backdrop-blur-lg border-y border-white/10">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <SectionHeader
            title="Exclusive Offers"
            subtitle="Handpicked deals, just for you. Don't miss out on these limited-time prices."
          />
          <div className="mt-10">
            <Carousel opts={{ align: "start", loop: true }}>
              <CarouselContent className="-ml-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <CarouselItem key={index} className="pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5">
                    <div className="p-1">
                      <Skeleton className="h-[360px] w-full rounded-2xl" />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !products || products.length === 0) {
    return null; // Don't render anything if there are no sale products or an error occurs
  }

  // Prepare plugins array - only include autoplay if it loaded successfully
  const plugins = autoplayPlugin ? [autoplayPlugin] : [];

  return (
    <div className="py-12 md:py-20 bg-white/5 backdrop-blur-lg border-y border-white/10">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <SectionHeader
          title="Exclusive Offers"
          subtitle="Handpicked deals, just for you. Don't miss out on these limited-time prices."
        />
        <div className="mt-10">
          <Carousel
            setApi={setApi}
            plugins={plugins}
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {products.map((product) => (
                <CarouselItem key={product.id} className="pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/5">
                  <div className="p-1 h-full">
                    <SaleProductCard product={product} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex" />
            <CarouselNext className="hidden sm:flex" />
          </Carousel>
        </div>
      </div>
    </div>
  );
};

export default OnSaleCarousel;
